import { useState, useEffect, useRef, useCallback } from "react";
import Header from "./Header";
// ─── Google Fonts (add to your index.html <head> if not already present) ───
// <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap" rel="stylesheet"/>



const FILTERS = [
  { key: "all", label: "All Work" },
  { key: "poster", label: "Poster Design" },
  { key: "website", label: "Website Developement" },
  { key: "packaging", label: "Packaging" },
  { key: "branding", label: "Branding" },
  { key: "social", label: "Social Media" },
  { key: "logo", label: "Logo Design" },
];

const STATS = [
  { target: 120, label: "Projects Delivered", suffix: "+" },
  { target: 48, label: "Happy Brands", suffix: "+" },
  { target: 4, label: "Years Experience", suffix: "+" },
  { target: 98, label: "Client Satisfaction", suffix: "%" },
];

// ─── CAT STYLES MAP ───────────────────────────────────────────────────────────
const CAT_STYLE = {
  poster: { bg: "rgba(91,156,255,0.2)", color: "#5b9cff", border: "rgba(91,156,255,0.3)", label: "Poster Design" },
  packaging: { bg: "rgba(138,91,255,0.2)", color: "#8a5bff", border: "rgba(138,91,255,0.3)", label: "Packaging" },
  branding: { bg: "rgba(255,171,91,0.15)", color: "#ffab5b", border: "rgba(255,171,91,0.25)", label: "Branding" },
  social: { bg: "rgba(91,255,178,0.12)", color: "#5bffb2", border: "rgba(91,255,178,0.2)", label: "Social Media" },
  logo: { bg: "rgba(255,91,91,0.12)", color: "#ff7b7b", border: "rgba(255,91,91,0.2)", label: "Logo Design" },
  website: { bg: "rgba(91,156,255,0.2)", color: "#5b9cff", border: "rgba(91,156,255,0.3)", label: "Website Developement" },
};

const GRAD_BG = {
  grad1: "linear-gradient(135deg,#1a2a4a 0%,#0d1826 100%)",
  grad2: "linear-gradient(135deg,#1e1040 0%,#120a2e 100%)",
  grad3: "linear-gradient(135deg,#0d2233 0%,#061520 100%)",
  grad4: "linear-gradient(135deg,#1a1040 0%,#2a0a40 100%)",
  grad5: "linear-gradient(135deg,#0a1f38 0%,#0f2a50 100%)",
  grad6: "linear-gradient(135deg,#16082e 0%,#220d46 100%)",
};

// ─── ARROW ICON ───────────────────────────────────────────────────────────────
const ArrowIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M7 17L17 7M17 7H7M17 7v10" />
  </svg>
);

// ─── COUNTER HOOK ─────────────────────────────────────────────────────────────
function useCounter(target, isVisible) {
  const [count, setCount] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (!isVisible || started.current) return;
    started.current = true;
    let current = 0;
    const step = target / 60;
    const timer = setInterval(() => {
      current = Math.min(current + step, target);
      setCount(Math.floor(current));
      if (current >= target) clearInterval(timer);
    }, 22);
    return () => clearInterval(timer);
  }, [isVisible, target]);

  return count;
}

// ─── STAT ITEM ────────────────────────────────────────────────────────────────
function StatItem({ stat, isVisible }) {
  const count = useCounter(stat.target, isVisible);
  return (
    <div style={styles.statItem}>
      <div style={styles.statNum}>
        {count}{stat.suffix}
      </div>
      <div style={styles.statLabel}>{stat.label}</div>
    </div>
  );
}

// ─── THUMB PLACEHOLDER ────────────────────────────────────────────────────────
function ThumbPlaceholder({ grad, emoji, height = "100%" }) {
  return (
    <div
      style={{
        ...styles.thumbPlaceholder,
        background: GRAD_BG[grad],
        height,
      }}
    >
      <div style={{ ...styles.shapeDecor, ...styles.shapeS1 }} />
      <div style={{ ...styles.shapeDecor, ...styles.shapeS2 }} />

      {emoji?.startsWith("http") ? (
        <img
          src={emoji}
          alt=""
          style={styles.thumbImg} // ✅ IMPORTANT
        />
      ) : (
        <span style={styles.emoji}>{emoji}</span>
      )}
    </div>
  );
}

// ─── PORTFOLIO CARD ───────────────────────────────────────────────────────────
function PortCard({ project, index, onClick }) {
  const [hovered, setHovered] = useState(false);
  const cat = CAT_STYLE[project.cat];

  return (
    <div
      onClick={() => onClick(project)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...styles.card,
        ...(project.wide ? styles.cardWide : {}),
        transform: hovered ? "translateY(-8px) scale(1.01)" : "translateY(0) scale(1)",
        boxShadow: hovered
          ? "0 24px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(91,156,255,0.2)"
          : "0 10px 30px rgba(0,0,0,0.35)",
        animationDelay: `${0.1 + index * 0.07}s`,
      }}
      className="port-card-anim"
    >
      {/* Thumbnail */}
      <div style={{ ...styles.cardThumb, height: project.tall ? 340 : 220 }}>
        <ThumbPlaceholder grad={project.grad} emoji={project.emoji} />

        {/* Category pill */}
        <span style={{
          ...styles.catPill,
          background: cat.bg,
          color: cat.color,
          border: `1px solid ${cat.border}`,
        }}>
          {cat.label}
        </span>

        {/* Hover overlay */}
        <div style={{ ...styles.cardOverlay, opacity: hovered ? 1 : 0 }}>
          <span style={styles.overlayAction}>
            View Project{" "}
            <span style={{ transition: "transform 0.2s", display: "inline-flex", transform: hovered ? "translate(3px,-3px)" : "none" }}>
              <ArrowIcon size={14} />
            </span>
          </span>
        </div>
      </div>

      {/* Body */}
      <div style={styles.cardBody}>
        <div style={styles.cardTitle}>{project.title}</div>
        <div style={styles.cardDesc}>{project.shortDesc}</div>
        <div style={styles.tagRow}>
          {/* {project.tags.map(t => <span key={t} style={styles.tag}>{t}</span>)} */}
          {Array.isArray(project.tags)
            ? project.tags.map(t => <span key={t} style={styles.tag}>{t}</span>)
            : project.tags?.split(",").map(t => <span key={t} style={styles.tag}>{t}</span>)
          }
        </div>
      </div>
    </div>
  );
}

// ─── LIGHTBOX ─────────────────────────────────────────────────────────────────
function Lightbox({ project, onClose }) {
  const open = !!project;

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  if (!project) return null;
  const cat = CAT_STYLE[project.cat];

  return (
    <div
      style={{ ...styles.lightbox, opacity: open ? 1 : 0, pointerEvents: open ? "all" : "none" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ ...styles.lbInner, transform: open ? "scale(1) translateY(0)" : "scale(0.92) translateY(20px)" }}>
        {/* Image */}
        <div style={styles.lbImgWrap}>
          <ThumbPlaceholder grad={project.grad} emoji={project.emoji} height="100%" />
        </div>

        {/* Close */}
        <button style={styles.lbClose} onClick={onClose}>✕</button>

        {/* Content */}
        <div style={styles.lbBody}>
          <span style={{
            ...styles.catPill,
            position: "relative", top: "auto", left: "auto",
            display: "inline-block", marginBottom: 10,
            background: cat.bg, color: cat.color, border: `1px solid ${cat.border}`,
          }}>
            {cat.label}
          </span>
          <div style={styles.lbTitle}>{project.title}</div>
          <div style={styles.lbDesc}>{project.desc}</div>
          <div style={styles.lbMeta}>
            {[
              { label: "Client", val: project.client },
              { label: "Year", val: project.year },
              { label: "Services", val: project.services },
            ].map(m => (
              <div key={m.label} style={styles.lbMetaItem}>
                <span style={styles.lbMetaLabel}>{m.label}</span>
                <span style={styles.lbMetaVal}>{m.val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function Portfolio() {
  const [activeFilter, setActiveFilter] = useState("all");
  const [selectedProject, setSelectedProject] = useState(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef(null);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("https://script.google.com/macros/s/AKfycbyhIURnC7ju1yCHa6i7HMki_Rvmm-643bPIZZpUsZOaC7EKyrm06Q4CT7fBWwOFlw/exec")
      .then(res => res.json())
      .then(data => {
        setProjects(data);
        setLoading(false);
        console.log("--------", data)
      })
      .catch(err => {
        console.error("API Error:", err);
        setLoading(false);
      });
  }, []);
  // Intersection observer for stats
  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const filtered = activeFilter === "all"
    ? projects
    : projects.filter(p => p.cat === activeFilter);

  const handleClose = useCallback(() => setSelectedProject(null), []);

  // if (loading) {
  //   return <h2 style={{ color: "white", textAlign: "center"  }}>Loading Projects...</h2>;
  // }
  return (
    <>

      <Header />
      {/* ── Inject styles ── */}
      <style>{CSS}</style>

      <section id="portfolio" style={styles.section}>
        {/* Glow blobs (via CSS pseudos, recreated as divs) */}
        <div style={styles.blob1} />
        <div style={styles.blob2} />

        {/* ── Header ── */}
        <div className="port-header-anim" style={styles.header}>
          <div style={styles.eyebrow}>
            <span style={styles.eyebrowLine} />
            Our Work
            <span style={styles.eyebrowLine} />
          </div>
          <h1 style={styles.title}>
            Creative Work That <br />
            <span style={styles.gradientWord}>Speaks Louder</span>
          </h1>
          <p style={styles.subtitle}>
            From bold brand identities to scroll-stopping packaging — here's what we've crafted for our clients.
          </p>
        </div>

        {/* ── Filter Bar ── */}
        {loading ? <h2 style={{ color: "white", textAlign: "center" }}>Loading Projects...</h2> : <>
          <div className="filter-bar-anim" style={styles.filterBar}>
            {FILTERS.map(f => (
              <FilterBtn
                key={f.key}
                filter={f}
                active={activeFilter === f.key}
                onClick={() => setActiveFilter(f.key)}
              />
            ))}
          </div>

          {/* ── Grid ── */}
          <div style={styles.grid}>
            {filtered.map((project, i) => (
              <PortCard
                key={project.id}
                project={project}
                index={i}
                onClick={setSelectedProject}
              />
            ))}
          </div>


        </>}
          {/* ── Stats ── */}
          <div
            ref={statsRef}
            className="stats-reveal"
            style={{
              ...styles.statsBar,
              opacity: statsVisible ? 1 : 0,
              transform: statsVisible ? "translateY(0)" : "translateY(30px)",
            }}
          >
            {STATS.map(s => (
              <StatItem key={s.label} stat={s} isVisible={statsVisible} />
            ))}
          </div>
        {/* ── CTA ── */}
        <div className="cta-anim" style={styles.cta}>
          <p style={{ color: "var(--muted)", fontSize: "0.95rem", marginBottom: 20 }}>
            Have a project in mind? Let's create something amazing together.
          </p>
          <a href="#contact" style={styles.ctaBtn}>
            Start a Project
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </section>

      {/* ── Lightbox ── */}
      <Lightbox project={selectedProject} onClose={handleClose} />
    </>
  );
}

// ─── FILTER BUTTON ────────────────────────────────────────────────────────────
function FilterBtn({ filter, active, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...styles.filterBtn,
        ...(active ? styles.filterBtnActive : {}),
        ...(!active && hovered ? styles.filterBtnHover : {}),
      }}
    >
      {filter.label}
    </button>
  );
}

// ─── STYLES ───────────────────────────────────────────────────────────────────
const styles = {
  thumbImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",   // 🔥 BEST LOOK
    display: "block",
  },
  section: {
    padding: "110px 5% 100px",
    minHeight: "100vh",
    position: "relative",
    overflow: "hidden",
    // background: "var(--bg)",
    background: '#020206',
    color: "var(--text)",
    fontFamily: "'DM Sans', sans-serif",
  },
  blob1: {
    position: "absolute",
    width: 500, height: 500,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(91,156,255,0.08), transparent 70%)",
    filter: "blur(120px)",
    top: -80, left: -100,
    pointerEvents: "none",
    zIndex: 0,
    animation: "blobDrift 12s ease-in-out infinite alternate",
  },
  blob2: {
    position: "absolute",
    width: 400, height: 400,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(138,91,255,0.09), transparent 70%)",
    filter: "blur(120px)",
    bottom: 0, right: -80,
    pointerEvents: "none",
    zIndex: 0,
    animation: "blobDrift 15s ease-in-out infinite alternate-reverse",
  },
  header: {
    textAlign: "center",
    marginBottom: 60,
    position: "relative",
    zIndex: 1,
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: "0.75rem",
    fontWeight: 600,
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    color: "var(--brand)",
    marginBottom: 14,
  },
  eyebrowLine: {
    display: "inline-block",
    width: 28, height: 1.5,
    background: "linear-gradient(90deg, var(--brand), var(--brand-2))",
    borderRadius: 2,
  },
  title: {
    fontFamily: "sans-serif",
    fontSize: "clamp(2.4rem, 5vw, 4rem)",
    fontWeight: 800,
    lineHeight: 1.08,
    color: "var(--text)",
    marginBottom: 16,
  },
  gradientWord: {
    background: "linear-gradient(135deg, var(--brand) 0%, var(--brand-2) 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  subtitle: {
    color: "var(--muted)",
    fontSize: "1rem",
    maxWidth: 480,
    margin: "0 auto",
    lineHeight: 1.65,
  },
  filterBar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    justifyContent: "center",
    marginBottom: 52,
    position: "relative",
    zIndex: 1,
  },
  filterBtn: {
    padding: "9px 22px",
    borderRadius: 50,
    border: "1.5px solid rgba(255,255,255,0.08)",
    background: "var(--surface)",
    color: "var(--muted)",
    fontFamily: "'DM Sans', sans-serif",
    fontSize: "0.82rem",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.25s ease",
    letterSpacing: "0.02em",
  },
  filterBtnHover: {
    borderColor: "var(--brand)",
    color: "var(--brand)",
  },
  filterBtnActive: {
    background: "linear-gradient(135deg, var(--brand), var(--brand-2))",
    borderColor: "transparent",
    color: "#fff",
    boxShadow: "0 4px 18px rgba(91,156,255,0.35)",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 22,
    position: "relative",
    zIndex: 1,
  },
  card: {
    borderRadius: 14,
    overflow: "hidden",
    background: "var(--surface)",
    border: "1px solid rgba(255,255,255,0.05)",
    cursor: "pointer",
    position: "relative",
    transition: "transform 0.4s cubic-bezier(.23,1,.32,1), box-shadow 0.4s",
  },
  cardWide: {
    gridColumn: "span 2",
  },
  cardThumb: {
    overflow: "hidden",
    position: "relative",
  },
  thumbPlaceholder: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "2.6rem",
    position: "relative",
    overflow: "hidden",
  },
  shapeDecor: {
    position: "absolute",
    borderRadius: "50%",
    opacity: 0.15,
  },
  shapeS1: {
    width: 120, height: 120,
    background: "var(--brand)",
    top: -30, right: -20,
  },
  shapeS2: {
    width: 80, height: 80,
    background: "var(--brand-2)",
    bottom: 10, left: 10,
  },
  emoji: {
    position: "relative",
    zIndex: 1,
    filter: "drop-shadow(0 2px 12px rgba(0,0,0,0.5))",
  },
  catPill: {
    position: "absolute",
    top: 14, left: 14,
    padding: "4px 12px",
    borderRadius: 50,
    fontSize: "0.68rem",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    backdropFilter: "blur(10px)",
    zIndex: 2,
  },
  cardOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to top, rgba(11,15,20,0.92) 0%, transparent 60%)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    padding: 20,
    transition: "opacity 0.35s ease",
    zIndex: 2,
  },
  overlayAction: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    color: "var(--brand)",
    fontSize: "0.8rem",
    fontWeight: 600,
    letterSpacing: "0.04em",
  },
  cardBody: {
    padding: "18px 20px 20px",
  },
  cardTitle: {
    fontFamily: " sans-serif",
    fontSize: "1.05rem",
    fontWeight: 700,
    color: "var(--text)",
    marginBottom: 6,
    lineHeight: 1.3,
  },
  cardDesc: {
    fontSize: "0.82rem",
    color: "var(--muted)",
    lineHeight: 1.55,
    marginBottom: 14,
  },
  tagRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  tag: {
    padding: "3px 10px",
    borderRadius: 4,
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.07)",
    fontSize: "0.7rem",
    color: "var(--muted)",
    fontWeight: 500,
  },
  statsBar: {
    display: "flex",
    gap: 0,
    marginTop: 60,
    background: "var(--surface)",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.06)",
    overflow: "hidden",
    position: "relative",
    zIndex: 1,
    transition: "opacity 0.7s ease, transform 0.7s ease",
  },
  statItem: {
    flex: 1,
    padding: "32px 20px",
    textAlign: "center",
    borderRight: "1px solid rgba(255,255,255,0.05)",
  },
  statNum: {
    fontFamily: "'Syne', sans-serif",
    fontSize: "2.2rem",
    fontWeight: 800,
    background: "linear-gradient(135deg, var(--brand), var(--brand-2))",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    lineHeight: 1,
    marginBottom: 6,
  },
  statLabel: {
    fontSize: "0.8rem",
    color: "var(--muted)",
    fontWeight: 500,
    letterSpacing: "0.03em",
  },
  cta: {
    textAlign: "center",
    marginTop: 64,
    position: "relative",
    zIndex: 1,
  },
  ctaBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "14px 32px",
    borderRadius: 50,
    background: "linear-gradient(135deg, var(--brand), var(--brand-2))",
    color: "#fff",
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 600,
    fontSize: "0.95rem",
    textDecoration: "none",
    // boxShadow: "0 6px 28px rgba(91,156,255,0.35)",
    transition: "transform 0.2s, box-shadow 0.2s",
    letterSpacing: "0.02em",
  },
  // Lightbox
  lightbox: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "rgba(7,10,15,0.96)",
    backdropFilter: "blur(18px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 30,
    transition: "opacity 0.35s ease",
  },
  lbInner: {
    maxWidth: 820,
    width: "100%",
    background: "var(--surface)",
    borderRadius: 20,
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.07)",
    boxShadow: "0 40px 100px rgba(0,0,0,0.7)",
    transition: "transform 0.4s cubic-bezier(.23,1,.32,1)",
    position: "relative",
  },
  lbImgWrap: {
    height: 380,
    overflow: "hidden",
    position: "relative",
  },
  lbClose: {
    position: "absolute",
    top: 16, right: 16,
    zIndex: 10,
    width: 38, height: 38,
    borderRadius: "50%",
    background: "rgba(0,0,0,0.5)",
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "#fff",
    fontSize: "1.1rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "background 0.2s",
  },
  lbBody: {
    padding: "28px 32px 32px",
  },
  lbTitle: {
    fontFamily: "sans-serif",
    fontSize: "1.65rem",
    fontWeight: 800,
    marginBottom: 10,
    color: "var(--text)",
  },
  lbDesc: {
    color: "var(--muted)",
    fontSize: "0.92rem",
    lineHeight: 1.65,
    marginBottom: 20,
  },
  lbMeta: {
    display: "flex",
    gap: 28,
    flexWrap: "wrap",
  },
  lbMetaItem: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  lbMetaLabel: {
    fontSize: "0.68rem",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "var(--muted)",
    fontWeight: 600,
  },
  lbMetaVal: {
    fontSize: "0.9rem",
    color: "var(--text)",
    fontWeight: 500,
  },
};

// ─── INJECTED CSS (keyframes + responsive + card animation) ──────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

  @keyframes blobDrift {
    from { transform: translate(0,0) scale(1); }
    to   { transform: translate(40px,30px) scale(1.1); }
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(30px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .port-header-anim  { animation: fadeUp 0.8s cubic-bezier(.23,1,.32,1) 0.1s both; }
  .filter-bar-anim   { animation: fadeUp 0.7s cubic-bezier(.23,1,.32,1) 0.3s both; }
  .cta-anim          { animation: fadeUp 0.7s cubic-bezier(.23,1,.32,1) 0.5s both; }
  .stats-reveal      { transition: opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s; }

  .port-card-anim {
    animation: fadeUp 0.7s cubic-bezier(.23,1,.32,1) both;
  }

  /* Responsive grid */
  @media (max-width: 900px) {
    #portfolio .grid-responsive { grid-template-columns: repeat(2,1fr) !important; }
    .card-wide-resp { grid-column: span 2 !important; }
  }
  @media (max-width: 580px) {
    #portfolio { padding: 80px 4% 80px !important; }
    #portfolio .grid-responsive { grid-template-columns: 1fr !important; }
    .card-wide-resp { grid-column: span 1 !important; }
    .stats-bar-resp { flex-direction: column !important; }
    .stats-bar-resp > div { border-right: none !important; border-bottom: 1px solid rgba(255,255,255,0.05); }
  }
`;
